import { useQuery, useMutation, useLazyQuery, ApolloError } from '@apollo/client';
import { GET_SEGMENT_FOR_BUILDER, ESTIMATE_SEGMENT_DEFINITION_QUERY } from '@/queries/segments';
import { UPDATE_SEGMENT_MUTATION } from '@/mutations/segments';
import { toast } from '@/hooks/use-toast';
import type {
  FindSegmentQueryResult,
  EventCondition,
  CreateSegmentInput,
  SegmentDefinitionInput
} from '@/gql/graphql';
import { SegmentOperator as SegmentOperatorEnum, ConditionTypeEnum } from '@/gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import {
  useSegmentBuilderStore,
  ConditionGroupState,
  ConditionState as StoreConditionState
} from '@/stores/segmentBuilder.store';

// Represents the state for a single condition *input* or *display* within the hook/UI if needed
// The store's ConditionState is the source of truth
// This local type might still be useful for forms or temporary state before committing to the store
export type LocalConditionState = Partial<EventCondition> & {
  conditionType: ConditionTypeEnum;
};

// ConditionGroupState is now imported from the store
// export interface ConditionGroupState { ... } // Removed local definition

// State for the Add Condition Dialog (remains local UI state)
interface AddConditionDialogState {
  isOpen: boolean;
  targetGroupClientId: string | null; // Use clientId instead of index
}

// Return type for the useSegmentBuilder hook
interface UseSegmentBuilderResult {
  // State from Apollo Query (Segment Fetch)
  loading: boolean; // Initial fetch loading
  fetchError: ApolloError | undefined; // Initial fetch error

  // State from Apollo Mutation (Segment Update)
  updateError: ApolloError | undefined; // GQL Mutation hook error (kept for potential direct GQL issues)

  // State from Apollo Lazy Query (Estimation)
  // Note: Components will get count/error from store, but hook provides loading status
  estimateLoading: boolean;
  estimateError: ApolloError | undefined;

  // Local UI State
  addConditionDialogState: AddConditionDialogState;

  // --- Orchestration Handlers & Store Management ---
  // Handler to trigger the save process (uses store action internally)
  handleSave: () => Promise<void>;
  // Handler to trigger estimate refresh (uses store action internally)
  refreshEstimateHandler: () => void;
  // Function to reset the zustand store
  resetStore: () => void;
  // Handlers for the local dialog state
  openAddConditionDialog: (groupClientId: string) => void;
  closeAddConditionDialog: () => void;
}

/**
 * Maps the raw segment data fetched from the API to the initial state
 * needed for the segment builder STORE.
 * Ensures clientIds are added if missing (though store's initialize should handle this).
 * @param apiData - The segment data returned from the GraphQL query.
 * @returns An object containing the initial state for the store.
 */
function mapApiToStoreInitializeData(apiData: FindSegmentQueryResult | null | undefined): {
  name: string;
  description: string;
  rootOperator: SegmentOperatorEnum;
  groups: ConditionGroupState[]; // Should match store's expected group structure
} {
  const name = apiData?.name ?? '';
  const description = apiData?.description ?? '';
  let rootOperator = SegmentOperatorEnum.And; // Default
  let groups: ConditionGroupState[] = [];

  const segmentDef = apiData?.segment;

  if (segmentDef) {
    rootOperator = segmentDef.rootOperator;

    if (Array.isArray(segmentDef.groups)) {
      groups = segmentDef.groups.map((group) => {
        const conditions = Array.isArray(group.conditions)
          ? group.conditions
              .filter(
                (cond): cond is EventCondition => cond.conditionType === ConditionTypeEnum.Event
              ) // Filter for event type
              .map((cond): StoreConditionState => {
                // Map API EventCondition to StoreConditionState
                return {
                  clientId: crypto.randomUUID(),
                  conditionType: ConditionTypeEnum.Event,
                  eventName: cond.eventName,
                  operator: cond.operator,
                  numTimes: cond.numTimes,
                  timeBoundary: cond.timeBoundary
                };
              })
          : [];

        // Return the mapped group state compatible with store's ConditionGroupState
        return {
          id: group.id ?? undefined, // DB ID
          clientId: crypto.randomUUID(),
          operator: group.operator,
          conditions: conditions
        };
      });
    }
  }

  return { name, description, rootOperator, groups };
}

/**
 * Maps the current store state (read directly from store)
 * into the structure required by the EstimateSegmentDefinitionInput GQL type.
 * @param name - Current segment name state from store.
 * @param description - Current segment description state from store.
 * @param rootOperator - Current root operator state from store.
 * @param groups - Current groups state array from store.
 * @returns The formatted input object for the estimateSegmentDefinition query.
 */

interface SegmentBuilderQueryResponse {
  getSegmentMetadataBySegmentId: FindSegmentQueryResult | null;
}

/**
 * Custom hook to manage the state and logic for the Segment Builder page.
 * Leverages useSegmentBuilderStore for core state management.
 * Handles fetching initial data, triggering saves/estimations via store/Apollo.
 *
 * @param segmentId The ID of the segment to fetch and edit.
 * @param merchantId The ID of the merchant, required for estimation.
 * @returns An object containing the segment state, loading/error states,
 *          and handlers for interaction.
 */
export function useSegmentBuilder(
  segmentId: string | undefined,
  merchantId: string | undefined
): UseSegmentBuilderResult {
  // --- Zustand Store Hook (used for actions and initialization) ---
  const {
    groups, // Still needed for estimation mapping
    isInitialized,
    isDirty,
    saveError: storeSaveError, // Keep for fallback error message in handleSave
    initialize: initializeStore,
    reset: resetStore, // Expose reset action
    saveSegment: saveSegmentInStore, // Get store save action
    fetchEstimate: fetchEstimateInStore // Get store estimate action (assuming implemented)
  } = useSegmentBuilderStore();

  // --- Apollo Hooks (Remain here) ---
  const {
    data: fetchedData,
    loading: fetchLoading,
    error: fetchError,
    refetch
  } = useQuery<SegmentBuilderQueryResponse>(GET_SEGMENT_FOR_BUILDER, {
    variables: { id: segmentId },
    skip: !segmentId,
    fetchPolicy: 'cache-and-network'
  });

  const [updateSegmentMutation, { error: updateError }] = useMutation<
    { updateSegment: string },
    { segment_id: string; input: CreateSegmentInput }
  >(UPDATE_SEGMENT_MUTATION);

  const [fetchEstimateQuery, { loading: estimateLoading, error: estimateError }] = useLazyQuery<
    { estimateSegmentDefinition: number },
    { input: { merchantId: string; segmentDefinition: SegmentDefinitionInput } }
  >(ESTIMATE_SEGMENT_DEFINITION_QUERY, {
    fetchPolicy: 'network-only'
  });

  // --- Local UI State (Dialog) ---
  const [addConditionDialogState, setAddConditionDialogState] = useState<AddConditionDialogState>({
    isOpen: false,
    targetGroupClientId: null
  });

  // --- Effects ---

  // Effect to initialize the store (Remains the same)
  useEffect(() => {
    if (segmentId && fetchedData?.getSegmentMetadataBySegmentId && !isInitialized) {
      const segmentData = fetchedData.getSegmentMetadataBySegmentId;
      const mappedStoreData = mapApiToStoreInitializeData(segmentData);
      initializeStore(mappedStoreData);
    }
  }, [segmentId, fetchedData, isInitialized, initializeStore]);

  // Optional reset effect (Remains the same)
  useEffect(() => {
    return () => {
      // resetStore(); // Consider if reset is always desired on ID change/unmount
    };
  }, [segmentId /* , resetStore */]);

  // Estimation trigger effect (Remains the same - relies on store state)
  // Debounce setup
  const triggerEstimate = useCallback(
    () => {
      if (!merchantId) {
        console.error('Missing merchantId needed for estimation');
        return;
      }
      // Call the STORE's fetchEstimate action, passing the query function
      void fetchEstimateInStore(merchantId, fetchEstimateQuery); // Updated to call store action + ADDED void
    },
    [merchantId, fetchEstimateQuery, fetchEstimateInStore] // Add dependencies
  );

  const debouncedTriggerEstimate = useMemo(() => {
    return debounce(() => {
      triggerEstimate();
    }, 750);
  }, [triggerEstimate]);

  // Effect to trigger estimation based on store state changes
  useEffect(() => {
    if (fetchLoading || !isInitialized) {
      return;
    }
    if (isDirty) {
      if (groups.length > 0) {
        debouncedTriggerEstimate();
      }
    }
    return () => {
      debouncedTriggerEstimate.cancel();
    };
  }, [groups, isDirty, isInitialized, fetchLoading, debouncedTriggerEstimate]);

  // --- Handlers ---

  const handleOpenAddConditionDialog = useCallback((groupClientId: string) => {
    setAddConditionDialogState({ isOpen: true, targetGroupClientId: groupClientId });
  }, []);
  const handleCloseAddConditionDialog = useCallback(() => {
    setAddConditionDialogState({ isOpen: false, targetGroupClientId: null });
  }, []);

  // --- Orchestration Handlers ---

  // Manual Estimate Refresh Handler (Calls store action)
  const manualFetchEstimate = useCallback(() => {
    if (fetchLoading || !isInitialized || !merchantId) {
      console.warn(
        'Cannot manually refresh estimate yet: Data missing, loading, or not initialized.'
      );
      return;
    }
    // Trigger the store action, passing the query function
    void fetchEstimateInStore(merchantId, fetchEstimateQuery); // Use store action
  }, [merchantId, fetchEstimateQuery, fetchEstimateInStore, fetchLoading, isInitialized]); // Add dependencies

  // Save Handler (Calls store action, passing mutation function)
  const handleSave = useCallback(async () => {
    if (!segmentId) {
      console.error('Cannot save: segmentId not available.');
      toast({
        title: 'Error',
        description: 'Cannot save segment: Missing ID.',
        variant: 'destructive'
      });
      return;
    }
    if (!isDirty) {
      console.log('No changes to save.');
      toast({ title: 'Info', description: 'No changes detected to save.' });
      return;
    }

    const success = await saveSegmentInStore(segmentId, updateSegmentMutation);

    if (success) {
      toast({ title: 'Success', description: 'Segment updated successfully.' });
      await refetch();
    } else {
      const message =
        storeSaveError ?? updateError?.message ?? 'An unknown error occurred during save.';
      toast({ title: 'Save Failed', description: message, variant: 'destructive' });
    }
  }, [
    segmentId,
    isDirty,
    saveSegmentInStore,
    updateSegmentMutation,
    refetch,
    storeSaveError,
    updateError
  ]);

  // --- Final Return Value (Reduced Set) ---
  return {
    // Initial fetch state
    loading: fetchLoading,
    fetchError,

    // Apollo hook errors
    updateError,
    estimateLoading,
    estimateError,

    // Local UI state & handlers
    addConditionDialogState,
    openAddConditionDialog: handleOpenAddConditionDialog,
    closeAddConditionDialog: handleCloseAddConditionDialog,

    // Orchestration handlers
    handleSave,
    refreshEstimateHandler: manualFetchEstimate,

    // Store management
    resetStore
  };
}
